/**
 * Blobbi Egg Animations
 * Self-contained CSS for egg visual system
 */

/* ==========================================
   Egg-specific animations
   ========================================== */

@keyframes egg-gentle-sway {
  0%,
  100% {
    transform: rotate(-1deg);
  }
  50% {
    transform: rotate(1deg);
  }
}

@keyframes egg-warmth-pulse {
  0%,
  100% {
    filter: brightness(1) drop-shadow(0 0 10px rgba(251, 191, 36, 0.3));
  }
  50% {
    filter: brightness(1.05) drop-shadow(0 0 20px rgba(251, 191, 36, 0.5));
  }
}

@keyframes egg-crack-shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-2px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(2px);
  }
}

/* Bouncy animation for singing - more energetic than sway but still cute */
@keyframes egg-bounce {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
  }
  25% {
    transform: translateY(-2px) rotate(1deg);
  }
  50% {
    transform: translateY(0) rotate(0deg);
  }
  75% {
    transform: translateY(-2px) rotate(-1deg);
  }
}

.animate-egg-sway {
  /* Egg sway is gentler than baby/adult (1.8s vs 1.2s) but still musical */
  animation: egg-gentle-sway 1.8s ease-in-out infinite;
}

.animate-egg-bounce {
  /* Egg sing bounce - gentler than baby/adult (0.6s vs 0.5s) */
  animation: egg-bounce 0.6s ease-in-out infinite;
}

.animate-egg-warmth {
  animation: egg-warmth-pulse 2s ease-in-out infinite;
}

.animate-egg-crack {
  animation: egg-crack-shake 0.5s ease-in-out infinite;
}

/* Tap interaction: rock side-to-side and hop */
@keyframes egg-tap-wiggle {
  0% {
    transform: translateY(0) rotate(0deg);
  }
  15% {
    transform: translateY(-2px) rotate(-6deg);
  }
  30% {
    transform: translateY(-6px) rotate(5deg);
  }
  45% {
    transform: translateY(-3px) rotate(-4deg);
  }
  60% {
    transform: translateY(-1px) rotate(3deg);
  }
  75% {
    transform: translateY(0) rotate(-1.5deg);
  }
  90% {
    transform: translateY(0) rotate(0.5deg);
  }
  100% {
    transform: translateY(0) rotate(0deg);
  }
}

.animate-egg-tap-wiggle {
  animation: egg-tap-wiggle 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

/* ==========================================
   Egg Status Effect Animations
   ========================================== */

/* Sweat droplet - slides down and fades */
@keyframes egg-sweat-drop {
  0% {
    opacity: 0;
    transform: translateY(-2px);
  }
  20% {
    opacity: 1;
    transform: translateY(0);
  }
  80% {
    opacity: 1;
    transform: translateY(4px);
  }
  100% {
    opacity: 0;
    transform: translateY(6px);
  }
}

.animate-egg-sweat-drop {
  animation: egg-sweat-drop 2s ease-in-out infinite;
}

/* Dust particles - gentle float up */
@keyframes egg-dust-particle {
  0%, 100% {
    opacity: 0.3;
    transform: translateY(0);
  }
  50% {
    opacity: 0.6;
    transform: translateY(-3px);
  }
}

.animate-egg-dust-particle {
  animation: egg-dust-particle 2s ease-in-out infinite;
}

/* Dizzy spirals - rotate and float */
@keyframes egg-spiral {
  0% {
    opacity: 0.4;
    transform: rotate(0deg) translateY(0);
  }
  50% {
    opacity: 0.8;
    transform: rotate(180deg) translateY(-4px);
  }
  100% {
    opacity: 0.4;
    transform: rotate(360deg) translateY(0);
  }
}

.animate-egg-spiral {
  animation: egg-spiral 3s ease-in-out infinite;
}

/* Happy sparkles - twinkle effect */
@keyframes egg-sparkle {
  0%, 100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}

.animate-egg-sparkle {
  animation: egg-sparkle 1.5s ease-in-out infinite;
}

/* ==========================================
   Special Mark Animations
   ========================================== */

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translateX(100%);
    opacity: 0;
  }
}

@keyframes glow-pulse {
  0%,
  100% {
    filter: brightness(1) drop-shadow(0 0 2px #00ffe0);
  }
  50% {
    filter: brightness(1.3) drop-shadow(0 0 8px #00ffe0) drop-shadow(0 0 12px #00ffe0);
  }
}

@keyframes rune-glow {
  0%,
  100% {
    filter: brightness(1) drop-shadow(0 0 1px rgba(130, 85, 30, 0.7));
  }
  50% {
    filter: brightness(1.2) drop-shadow(0 0 4px rgba(130, 85, 30, 0.9))
      drop-shadow(0 0 8px rgba(130, 85, 30, 0.6));
  }
}

@keyframes sigil-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
}

@keyframes mystical-float {
  0%,
  100% {
    transform: translateY(0px) rotate(0deg);
  }
  33% {
    transform: translateY(-2px) rotate(1deg);
  }
  66% {
    transform: translateY(1px) rotate(-1deg);
  }
}

@keyframes crack-energy {
  0% {
    stroke-dasharray: 0 100;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 50 50;
    stroke-dashoffset: -25;
  }
  100% {
    stroke-dasharray: 100 0;
    stroke-dashoffset: -50;
  }
}

/* Animation classes */
.animate-shimmer {
  animation: shimmer 3s ease-in-out infinite;
}

.animate-glow-pulse {
  animation: glow-pulse 2s ease-in-out infinite;
}

.animate-rune-glow {
  animation: rune-glow 2.5s ease-in-out infinite;
}

.animate-sigil-pulse {
  animation: sigil-pulse 3s ease-in-out infinite;
}

.animate-mystical-float {
  animation: mystical-float 4s ease-in-out infinite;
}

.animate-crack-energy {
  animation: crack-energy 2s linear infinite;
}

/* ==========================================
   Special mark container styles
   ========================================== */

.special-mark-container {
  will-change: transform, opacity;
  backface-visibility: hidden;
  transform-style: preserve-3d;
}

/* Ensure special marks are subtle and well-positioned */
.special-mark-container svg {
  opacity: 0.8;
  filter: brightness(0.9);
}

/* Performance optimizations for special marks */
.special-mark-container svg {
  will-change: transform;
  transform: translateZ(0);
}

/* ==========================================
   Tour Visual State Animations
   ========================================== */

/* Shell opening: scale up slightly then fade out with blur */
@keyframes egg-tour-open {
  0% {
    transform: scale(1);
    opacity: 1;
    filter: brightness(1.1);
  }
  40% {
    transform: scale(1.05);
    opacity: 0.9;
    filter: brightness(1.4);
  }
  100% {
    transform: scale(1.15);
    opacity: 0;
    filter: brightness(2) blur(4px);
  }
}

.animate-egg-tour-open {
  animation: egg-tour-open 1.2s ease-in-out forwards;
}

/* Pulsing glow for the "waiting for click" tour state */
@keyframes egg-tour-glow {
  0%, 100% {
    opacity: 0.5;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
  }
}

.animate-egg-tour-glow {
  animation: egg-tour-glow 2s ease-in-out infinite;
}

/* ==========================================
   Responsive adjustments
   ========================================== */

@media (max-width: 640px) {
  .special-mark-container {
    /* Reduce animation intensity on mobile for better performance */
    animation-duration: 1.5s !important;
  }
}

/* ==========================================
   Accessibility: Reduced motion support
   ========================================== */

@media (prefers-reduced-motion: reduce) {
  .special-mark-container,
  .animate-shimmer,
  .animate-glow-pulse,
  .animate-rune-glow,
  .animate-sigil-pulse,
  .animate-mystical-float,
  .animate-crack-energy,
  .animate-egg-sway,
  .animate-egg-bounce,
  .animate-egg-warmth,
  .animate-egg-crack,
  .animate-egg-tap-wiggle,
  .animate-egg-sweat-drop,
  .animate-egg-dust-particle,
  .animate-egg-spiral,
  .animate-egg-sparkle,
  .animate-egg-tour-glow,
  .animate-egg-tour-open {
    animation: none !important;
  }
}

/* ==========================================
   High contrast mode support
   ========================================== */

@media (prefers-contrast: high) {
  .special-mark-container svg path,
  .special-mark-container svg circle,
  .special-mark-container svg ellipse {
    stroke-width: 3px !important;
    opacity: 0.9 !important;
  }
}

/* ==========================================
   Dark mode adjustments
   ========================================== */

@media (prefers-color-scheme: dark) {
  .special-mark-container svg {
    filter: brightness(1.2) contrast(1.1);
  }
}

/* ==========================================
   Print styles
   ========================================== */

@media print {
  .special-mark-container {
    animation: none !important;
    filter: none !important;
  }

  .special-mark-container svg {
    filter: grayscale(1) contrast(1.5) !important;
  }
}

/* ==========================================
   Onboarding Hatching Ceremony Animations
   ========================================== */

/* Soft breathing pulse for the egg before interaction */
@keyframes egg-onboard-breathe {
  0%, 100% {
    transform: scale(1);
    filter: brightness(1) drop-shadow(0 0 20px rgba(255, 255, 255, 0.08));
  }
  50% {
    transform: scale(1.015);
    filter: brightness(1.03) drop-shadow(0 0 30px rgba(255, 255, 255, 0.15));
  }
}

.animate-egg-onboard-breathe {
  animation: egg-onboard-breathe 3s ease-in-out infinite;
}

/* Screen-filling radial glow that expands from center on hatch */
@keyframes onboard-glow-expand {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  30% {
    opacity: 1;
  }
  100% {
    opacity: 0.85;
    transform: scale(2.5);
  }
}

.animate-onboard-glow-expand {
  animation: onboard-glow-expand 1.8s ease-out forwards;
}

/* Gentle lingering glow fade after hatch - holds then fades */
@keyframes onboard-glow-linger {
  0% {
    opacity: 0.85;
  }
  15% {
    opacity: 0.85;
  }
  100% {
    opacity: 0;
  }
}

.animate-onboard-glow-linger {
  animation: onboard-glow-linger 7s ease-out forwards;
}

/* Sentimental text fade in - very slow, dreamlike */
@keyframes onboard-text-reveal {
  0% {
    opacity: 0;
    transform: translateY(12px);
    filter: blur(4px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
}

.animate-onboard-text-reveal {
  animation: onboard-text-reveal 1.8s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

/* Delayed text reveal for secondary text */
.animate-onboard-text-reveal-delay {
  opacity: 0;
  animation: onboard-text-reveal 1.8s cubic-bezier(0.22, 1, 0.36, 1) 0.6s forwards;
}

/* Soft fade out for transition between phases */
@keyframes onboard-soft-fade-out {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.animate-onboard-soft-fade-out {
  animation: onboard-soft-fade-out 0.8s ease-out forwards;
}

/* Soft fade in */
@keyframes onboard-soft-fade-in {
  0% {
    opacity: 0;
    transform: translateY(8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-onboard-soft-fade-in {
  animation: onboard-soft-fade-in 1s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

/* Floating particles that drift upward from the egg */
@keyframes onboard-particle-rise {
  0% {
    opacity: 0;
    transform: translateY(0) scale(0.5);
  }
  20% {
    opacity: 0.8;
  }
  100% {
    opacity: 0;
    transform: translateY(-120px) scale(0.2);
  }
}

/* Sparkle twinkle - stays in place, pulses brightness */
@keyframes onboard-sparkle-twinkle {
  0%, 100% {
    opacity: 0;
    transform: scale(0.5);
  }
  15% {
    opacity: 1;
    transform: scale(1.2);
  }
  30% {
    opacity: 0.6;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
  70% {
    opacity: 0.3;
    transform: scale(0.6);
  }
  85% {
    opacity: 0.9;
    transform: scale(1.1);
  }
}

/* Sparkle drift - gentle floating motion */
@keyframes onboard-sparkle-drift {
  0% {
    opacity: 0;
    transform: translateY(0) scale(0.3);
  }
  20% {
    opacity: 1;
    transform: translateY(-8px) scale(1);
  }
  80% {
    opacity: 0.8;
    transform: translateY(-25px) scale(0.9);
  }
  100% {
    opacity: 0;
    transform: translateY(-40px) scale(0.4);
  }
}

/* Egg entrance - subtle float up from darkness */
@keyframes egg-onboard-entrance {
  0% {
    opacity: 0;
    transform: translateY(30px) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.animate-egg-onboard-entrance {
  animation: egg-onboard-entrance 1.5s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

/* Egg shake intensifying - for crack stages */
@keyframes egg-onboard-shake-light {
  0%, 100% { transform: translateX(0) rotate(0deg); }
  25% { transform: translateX(-3px) rotate(-2deg); }
  75% { transform: translateX(3px) rotate(2deg); }
}

@keyframes egg-onboard-shake-medium {
  0%, 100% { transform: translateX(0) rotate(0deg); }
  20% { transform: translateX(-5px) rotate(-3deg); }
  40% { transform: translateX(4px) rotate(2deg); }
  60% { transform: translateX(-4px) rotate(-2deg); }
  80% { transform: translateX(5px) rotate(3deg); }
}

@keyframes egg-onboard-shake-heavy {
  0%, 100% { transform: translateX(0) rotate(0deg); }
  10% { transform: translateX(-6px) rotate(-4deg); }
  20% { transform: translateX(5px) rotate(3deg); }
  30% { transform: translateX(-7px) rotate(-3deg); }
  40% { transform: translateX(6px) rotate(4deg); }
  50% { transform: translateX(-5px) rotate(-2deg); }
  60% { transform: translateX(7px) rotate(3deg); }
  70% { transform: translateX(-6px) rotate(-4deg); }
  80% { transform: translateX(5px) rotate(2deg); }
  90% { transform: translateX(-4px) rotate(-3deg); }
}

.animate-egg-onboard-shake-light {
  animation: egg-onboard-shake-light 0.4s ease-in-out;
}

.animate-egg-onboard-shake-medium {
  animation: egg-onboard-shake-medium 0.5s ease-in-out;
}

.animate-egg-onboard-shake-heavy {
  animation: egg-onboard-shake-heavy 0.6s ease-in-out;
}

/* Final burst - egg explodes into light */
@keyframes egg-onboard-burst {
  0% {
    transform: scale(1);
    opacity: 1;
    filter: brightness(1);
  }
  30% {
    transform: scale(1.08);
    filter: brightness(1.5);
  }
  60% {
    transform: scale(1.15);
    opacity: 0.8;
    filter: brightness(2.5);
  }
  100% {
    transform: scale(1.3);
    opacity: 0;
    filter: brightness(4) blur(8px);
  }
}

.animate-egg-onboard-burst {
  animation: egg-onboard-burst 1.2s ease-in-out forwards;
}

/* Screen flash on hatch */
@keyframes onboard-screen-flash {
  0% {
    opacity: 0;
  }
  15% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.animate-onboard-screen-flash {
  animation: onboard-screen-flash 2s ease-out forwards;
}

/* Gentle continue prompt pulse */
@keyframes onboard-continue-pulse {
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.7;
  }
}

.animate-onboard-continue-pulse {
  animation: onboard-continue-pulse 2.5s ease-in-out infinite;
}

/* Slow rotating golden incandescence behind hatched blobbi */
@keyframes onboard-golden-rotate {
  0% {
    transform: rotate(0deg) scale(1);
  }
  25% {
    transform: rotate(90deg) scale(1.06);
  }
  50% {
    transform: rotate(180deg) scale(1);
  }
  75% {
    transform: rotate(270deg) scale(1.06);
  }
  100% {
    transform: rotate(360deg) scale(1);
  }
}

.animate-onboard-golden-rotate {
  animation: onboard-golden-rotate 20s linear infinite;
}

/* Golden glow fade-in */
@keyframes onboard-golden-fadein {
  0% {
    opacity: 0;
    transform: scale(0.7);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-onboard-golden-fadein {
  animation: onboard-golden-fadein 2.5s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

/* ─── Evolve Ceremony Animations ─────────────────────────────────────────────── */

/* Energy particles spiral inward toward the blobbi */
@keyframes evolve-spiral-in {
  0% {
    opacity: 0;
    transform: rotate(0deg) translateX(250px) scale(0.5);
  }
  20% {
    opacity: 1;
  }
  80% {
    opacity: 0.8;
    transform: rotate(540deg) translateX(20px) scale(1);
  }
  100% {
    opacity: 0;
    transform: rotate(720deg) translateX(0px) scale(0);
  }
}

/* Pulsing glow behind baby during gather/surge */
@keyframes evolve-glow-pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.15);
    opacity: 1;
  }
}

/* Reduced motion overrides for onboarding */
@media (prefers-reduced-motion: reduce) {
  .animate-egg-onboard-breathe,
  .animate-onboard-glow-expand,
  .animate-onboard-glow-linger,
  .animate-onboard-text-reveal,
  .animate-onboard-text-reveal-delay,
  .animate-onboard-soft-fade-out,
  .animate-onboard-soft-fade-in,
  .animate-egg-onboard-entrance,
  .animate-egg-onboard-shake-light,
  .animate-egg-onboard-shake-medium,
  .animate-egg-onboard-shake-heavy,
  .animate-egg-onboard-burst,
  .animate-onboard-screen-flash,
  .animate-onboard-continue-pulse,
  .animate-onboard-golden-rotate,
  .animate-onboard-golden-fadein {
    animation: none !important;
    opacity: 1 !important;
    transform: none !important;
    filter: none !important;
  }

  /* Evolve ceremony uses inline animation styles, so target all descendants */
  [data-evolve-ceremony] *,
  [data-evolve-ceremony] {
    animation: none !important;
    transition: none !important;
  }
}
